import React, { useState } from 'react';
import { TextField, IconButton, InputAdornment, Snackbar, Alert } from '@mui/material';
import { AppButton } from '../../../shared/components/AppButton';
import { Visibility, VisibilityOff, VpnKey, Lock, VerifiedUser, LockReset } from '@mui/icons-material';
import { useAuth } from '../../../shared/contexts/AuthContext';

export const ChangePassword: React.FC = () => {
    const { changePassword } = useAuth();
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [hideCurrentPassword, setHideCurrentPassword] = useState(true);
    const [hideNewPassword, setHideNewPassword] = useState(true);
    const [hideConfirmPassword, setHideConfirmPassword] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

    const validate = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (!oldPassword) {
            newErrors.oldPassword = 'Current Password is required';
        }

        if (!newPassword) {
            newErrors.newPassword = 'New Password is required';
        } else if (newPassword.length < 6) {
            newErrors.newPassword = 'Password must be at least 6 characters long';
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = 'Confirm Password is required';
        } else if (newPassword !== confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        setIsLoading(true);

        try {
            const response = await changePassword({ oldPassword, newPassword });
            setSnackbar({
                open: true,
                message: response?.message || 'Password changed successfully!',
                severity: 'success'
            });
            resetForm();
        } catch (error: any) {
            setSnackbar({
                open: true,
                message: error?.response?.data?.message || 'Failed to change password',
                severity: 'error'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const resetForm = () => {
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setErrors({});
        setHideCurrentPassword(true);
        setHideNewPassword(true);
        setHideConfirmPassword(true);
    };

    const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    const inputSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',

            '& fieldset': {
                borderColor: '#d1d5db', // default grey
            },

            '&:hover fieldset': {
                borderColor: '#0EA5E9',
            },

            '&.Mui-focused fieldset': {
                borderColor: '#0EA5E9',
                borderWidth: '2px',
            },
        },

        // 🔸 Default label color
        '& .MuiInputLabel-root': {
            color: '#6b7280', // grey
        },

        // 🔸 Hover label color
        '&:hover .MuiInputLabel-root': {
            color: '#0EA5E9',
        },

        // 🔸 Focus label color (IMPORTANT)
        '& .MuiInputLabel-root.Mui-focused': {
            color: '#0EA5E9',
        },
    };



    return (
        <div className="change-password-container relative z-10 w-full max-w-lg sm:max-w-xl md:max-w-2xl lg:max-w-4xl mx-auto my-10 px-[15px]">
            <div className="change-password-card backdrop-blur-lg bg-white/70 rounded-3xl shadow-2xl p-4 sm:p-8 md:p-10">
                {/* Header */}
                <div className="header-section text-center mb-8">
                    <div className="icon-wrapper">
                        <LockReset className="text-white text-2xl" />
                    </div>
                    <h1 className="title text-3xl font-bold text-gray-800 mb-2">Change Password</h1>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="form flex flex-col gap-7" >
                    {/* Current Password */}
                    <TextField
                        label="Current Password"
                        className="kr-input "
                        type={hideCurrentPassword ? 'password' : 'text'}
                        value={oldPassword}
                        onChange={(e) => {
                            setOldPassword(e.target.value);
                            if (errors.oldPassword) {
                                setErrors({ ...errors, oldPassword: '' });
                            }
                        }}
                        placeholder="Enter your current password"
                        autoComplete="current-password"
                        fullWidth
                        variant="outlined"
                        error={!!errors.oldPassword}
                        helperText={errors.oldPassword}
                        sx={inputSx}
                        InputProps={{
                            startAdornment: <VpnKey className="mr-2" />,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setHideCurrentPassword(!hideCurrentPassword)}
                                        edge="end"
                                    >
                                        {hideCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    {/* New Password */}
                    <TextField
                        label="New Password"
                        type={hideNewPassword ? 'password' : 'text'}
                        value={newPassword}
                        onChange={(e) => {
                            setNewPassword(e.target.value);
                            if (errors.newPassword) {
                                setErrors({ ...errors, newPassword: '' });
                            }
                            // Re-validate confirm password if it's already set
                            if (confirmPassword && e.target.value !== confirmPassword) {
                                setErrors({ ...errors, confirmPassword: 'Passwords do not match' });
                            } else if (confirmPassword && e.target.value === confirmPassword) {
                                const newErrors = { ...errors };
                                delete newErrors.confirmPassword;
                                setErrors(newErrors);
                            }
                        }}
                        placeholder="Enter your new password"
                        autoComplete="new-password"
                        fullWidth
                        variant="outlined"
                        error={!!errors.newPassword}
                        helperText={errors.newPassword}
                        sx={inputSx}
                        InputProps={{
                            startAdornment: <Lock className="mr-2" />,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setHideNewPassword(!hideNewPassword)}
                                        edge="end"
                                    >
                                        {hideNewPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    {/* Confirm Password */}
                    <TextField
                        label="Confirm New Password"
                        type={hideConfirmPassword ? 'password' : 'text'}
                        value={confirmPassword}
                        onChange={(e) => {
                            setConfirmPassword(e.target.value);
                            if (e.target.value && e.target.value !== newPassword) {
                                setErrors({ ...errors, confirmPassword: 'Passwords do not match' });
                            } else {
                                const newErrors = { ...errors };
                                delete newErrors.confirmPassword;
                                setErrors(newErrors);
                            }
                        }}
                        placeholder="Confirm your new password"
                        autoComplete="new-password"
                        fullWidth
                        variant="outlined"
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword}
                        sx={inputSx}
                        InputProps={{
                            startAdornment: <VerifiedUser className="mr-2" />,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setHideConfirmPassword(!hideConfirmPassword)}
                                        edge="end"
                                    >
                                        {hideConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    {/* Action Buttons */}
                    <div className="action-buttons flex justify-end gap-4 mt-4">
                        <AppButton
                            type="button"
                            variant="cancel"
                            onClick={resetForm}
                        >
                            Cancel
                        </AppButton>
                        <AppButton
                            type="submit"
                            variant="primary"
                            disabled={
                                isLoading ||
                                !oldPassword ||
                                !newPassword ||
                                !confirmPassword ||
                                !!errors.confirmPassword
                            }
                            startIcon={<LockReset />}
                            loading={isLoading}
                        >
                            {isLoading ? 'Updating Password...' : 'Update Password'}
                        </AppButton>

                    </div>
                </form>
            </div>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ top: '80px !important' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{
                        minWidth: '300px',
                        maxWidth: '500px',
                        borderRadius: '12px',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                        fontSize: '0.95rem',
                        fontWeight: 500
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};
